/** Everything this node reaches, and everything reaching it.
 *
 * The other half of the graph fix. Hovering a hub used to paint a name for the
 * node AND every one of its neighbours onto the canvas at once, which is how a
 * 30-edge node became an unreadable stack of plates. The canvas now names only
 * what you are pointing at; the neighbours are read HERE, where a list can hold
 * thirty names without any of them landing on top of another.
 *
 * Grouped by edge type, because "similar to" and "mentions" are not the same
 * claim and a flat list hides that. Authored edges are named separately from
 * inferred ones for the same reason.
 */
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import * as React from "react";

import { useExpand } from "@/hooks/queries";
import { cn } from "@/lib/utils";
import { useGraph } from "@/stores/graph";

/** How an edge type reads to a person, and the colour it carries on the canvas. */
const EDGE_LABEL: Record<string, { text: string; tone: string }> = {
  similar_to: { text: "Similar to", tone: "text-viridian" },
  mentions: { text: "Mentions", tone: "text-orchid" },
  contains: { text: "Contains", tone: "text-muted-foreground" },
  co_located: { text: "Co-located with", tone: "text-amber-400" },
  relates_to: { text: "Relates to", tone: "text-skyb" },
};

function edgeMeta(type: string) {
  return EDGE_LABEL[type] ?? { text: type.replace(/_/g, " "), tone: "text-muted-foreground" };
}

export function ConnectionsList({ nodeId }: { nodeId: string }) {
  const version = useGraph((s) => s.version);
  const expand = useExpand();
  const [loading, setLoading] = React.useState(false);
  const asked = React.useRef<Set<string>>(new Set());

  // The panel is shared by the ATLAS and the ORBIT view, and only the atlas
  // fills the graph store. Opened from an orbit, the store held the selected
  // node and none of its edges, so this list confidently reported "nothing
  // links to this yet" about a node with thirty connections. Empty is a claim,
  // and it has to be earned: fetch the neighbourhood once, then say it.
  React.useEffect(() => {
    const { links } = useGraph.getState();
    for (const link of links.values()) {
      const src = typeof link.source === "object" ? (link.source as { id: string }).id : link.source;
      const dst = typeof link.target === "object" ? (link.target as { id: string }).id : link.target;
      if (src === nodeId || dst === nodeId) return;
    }
    if (asked.current.has(nodeId)) return;
    asked.current.add(nodeId);
    let live = true;
    setLoading(true);
    void expand(nodeId)
      .then((nb) => {
        if (live) useGraph.getState().mergeNeighborhood(nb);
      })
      .catch(() => {
        // A failed fetch must not read as "no connections" either; the empty
        // copy below stays honest because `asked` stops the retry loop and the
        // user can reopen the node.
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeId, version]);

  // Recomputed against `version` rather than subscribing to the maps, which are
  // mutated in place by mergeBatch.
  const groups = React.useMemo(() => {
    const { links, nodes } = useGraph.getState();
    const out = new Map<string, { id: string; label: string; incoming: boolean }[]>();
    for (const link of links.values()) {
      const src = typeof link.source === "object" ? (link.source as { id: string }).id : link.source;
      const dst = typeof link.target === "object" ? (link.target as { id: string }).id : link.target;
      if (src !== nodeId && dst !== nodeId) continue;
      const otherId = src === nodeId ? dst : src;
      const other = nodes.get(otherId as string);
      if (!other) continue;
      const key = link.edge.type ?? "relates_to";
      const list = out.get(key) ?? [];
      list.push({ id: other.id, label: other.label, incoming: dst === nodeId });
      out.set(key, list);
    }
    return [...out.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [nodeId, version]);

  const total = groups.reduce((n, [, list]) => n + list.length, 0);

  if (!total) {
    return (
      <section className="px-3 py-2">
        <h3 className="mb-1 font-mono text-[10px] uppercase tracking-[0.13em] text-muted-foreground">
          Connections
        </h3>
        {loading && (
          <p className="flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
            Looking…
          </p>
        )}
        {!loading && (
          <p className="text-[12.5px] leading-relaxed text-muted-foreground">
            Nothing links to this yet. Connections appear as the graph finds
            similar documents, or when you draw one yourself in the atlas.
          </p>
        )}
      </section>
    );
  }

  return (
    <section className="px-3 py-2">
      <div className="mb-2 flex items-baseline gap-2">
        <h3 className="font-mono text-[10px] uppercase tracking-[0.13em] text-muted-foreground">
          Connections
        </h3>
        <span className="text-[11px] text-muted-foreground">{total}</span>
      </div>
      <p className="mb-2.5 border-l-2 border-primary/60 bg-muted/40 py-1.5 pl-2.5 pr-2 text-[11.5px] leading-relaxed text-muted-foreground">
        Everything this reaches, and everything reaching it. Hovering a node in
        the atlas names only that node — its neighbours are read here.
      </p>

      <div className="flex flex-col gap-3">
        {groups.map(([type, list]) => {
          const meta = edgeMeta(type);
          return (
            <div key={type}>
              <div className="mb-1 flex items-baseline gap-2">
                <span className={cn("font-mono text-[9.5px] uppercase tracking-[0.1em]", meta.tone)}>
                  {meta.text}
                </span>
                <span className="text-[10px] text-muted-foreground">{list.length}</span>
              </div>
              <ul className="flex flex-col gap-0.5">
                {list.map((n) => (
                  <li key={`${type}:${n.id}:${n.incoming ? "in" : "out"}`}>
                    <button
                      type="button"
                      onClick={() => {
                        useGraph.getState().select({ kind: "node", id: n.id });
                        useGraph.getState().requestFocus(n.id);
                      }}
                      className="flex w-full items-center gap-2 rounded px-1.5 py-1 text-left text-[12.5px] text-foreground/90 transition-colors hover:bg-accent hover:text-foreground"
                    >
                      {n.incoming ? (
                        <ArrowLeft className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
                      ) : (
                        <ArrowRight className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
                      )}
                      <span className="min-w-0 flex-1 truncate">{n.label}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </section>
  );
}
